import { useState } from "react";
import { Mail, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { SocialLinks } from "../components/SocialLinks";

const EMAIL = "kitoe.entertainment@gmail.com";

export default function Contact() {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      toast.success("Email address copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — please select the email manually");
    }
  };

  return (
    <main>
      <section className="px-4 sm:px-8 pt-14 sm:pt-20 pb-16">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-coral">
            Contact
          </span>
          <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            <MaskedLines lines={["Let's talk about", "curious players"]} />
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-5 text-base sm:text-lg text-ink/70 max-w-xl mx-auto leading-relaxed">
              Feedback from players, ideas from teachers, bug reports, or just a hello
              from a fellow quiz lover — every message lands straight in my inbox.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-4 sm:px-8 pb-16">
        <Reveal className="max-w-3xl mx-auto">
          <div className="rounded-[2.5rem] border-2 border-ink bg-white shadow-brutal-lg p-8 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-12 -left-12 w-44 h-44 rounded-full bg-sun/40 blur-2xl" />
            <div className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full bg-skykit/30 blur-2xl" />

            <div className="relative">
              <span className="inline-flex w-16 h-16 rounded-2xl border-2 border-ink bg-sun items-center justify-center">
                <Mail size={28} />
              </span>
              <h2 className="mt-5 font-display font-black text-2xl sm:text-3xl tracking-tight">
                Write to us anytime
              </h2>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-link"
                className="mt-4 inline-block font-display font-extrabold text-xl sm:text-2xl text-coral underline decoration-sun decoration-4 underline-offset-4 hover:decoration-coral transition-colors break-all"
              >
                {EMAIL}
              </a>

              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <a
                  href={`mailto:${EMAIL}?subject=Hello%20KITOE%20entertainment!`}
                  data-testid="contact-send-email-button"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-coral text-white font-display font-extrabold text-lg border-2 border-ink shadow-brutal hover:-translate-y-1 hover:shadow-brutal-lg transition-all duration-200"
                >
                  <Mail size={20} /> Send an email
                </a>
                <button
                  onClick={copyEmail}
                  data-testid="contact-copy-email-button"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-white text-ink font-display font-extrabold text-lg border-2 border-ink shadow-brutal-sm hover:-translate-y-1 hover:shadow-brutal transition-all duration-200"
                >
                  {copied ? <Check size={20} className="text-mint" /> : <Copy size={20} />}
                  {copied ? "Copied!" : "Copy address"}
                </button>
              </div>

              <p className="mt-8 text-sm text-ink/50">
                We usually reply within a day or two. No spam, ever.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="px-4 sm:px-8 pb-24">
        <Reveal className="max-w-3xl mx-auto text-center">
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
            Follow the fun
          </h2>
          <p className="mt-3 text-ink/60 text-base sm:text-lg">
            Behind-the-scenes peeks, new game reveals and colouring previews.
          </p>
          <div className="mt-6 flex justify-center">
            <SocialLinks size="lg" />
          </div>
        </Reveal>
      </section>

      <Marquee />
    </main>
  );
}
